import { useEffect, useState } from "react";
import { Home, FolderKanban, FileText, Send, MessageSquare, BarChart3, Bell, Settings, LogOut, CreditCard, ChevronLeft, ChevronRight, Calendar, BookOpen, FolderOpen } from "lucide-react";
import { AnimatedLogo } from "@/components/ui/animated-logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { CurrencySwitcher } from "@/components/layout/CurrencySwitcher";

const menuItems = [
  { title: "Overview", tab: "overview", icon: Home },
  { title: "Projects", tab: "projects", icon: FolderKanban },
  { title: "My Requests", tab: "requests", icon: FileText },
  { title: "New Request", tab: "new-request", icon: Send },
  { title: "Documents", tab: "documents", icon: FolderOpen },
  { title: "Meetings", tab: "meetings", icon: Calendar },
  { title: "Invoices", tab: "invoices", icon: FileText },
  { title: "Payments", tab: "payments", icon: CreditCard },
  { title: "Chat", tab: "chat", icon: MessageSquare },
  { title: "Analytics", tab: "analytics", icon: BarChart3 },
  { title: "Knowledge Base", tab: "knowledge", icon: BookOpen },
];

interface ClientSidebarProps {
  onSignOut: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function ClientSidebar({
  onSignOut,
  activeTab,
  onTabChange,
  isCollapsed,
  onToggleCollapse,
}: ClientSidebarProps) {
  const { user } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (!user) return;
    
    fetchUnreadCounts();
    
    // Subscribe to notifications
    const notifChannel = supabase
      .channel('client-notif-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_notifications',
          filter: `client_id=eq.${user.id}`
        },
        () => fetchUnreadCounts()
      )
      .subscribe();

    // Subscribe to messages
    const msgChannel = supabase
      .channel('client-msg-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'client_support_messages',
          filter: `client_id=eq.${user.id}`
        },
        () => fetchUnreadCounts()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(notifChannel);
      supabase.removeChannel(msgChannel);
    };
  }, [user]);

  const fetchUnreadCounts = async () => {
    if (!user) return;
    
    // Get unread notifications
    const { count: notifCount } = await supabase
      .from('client_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('client_id', user.id)
      .eq('read', false);

    // Get unread messages
    const { count: msgCount } = await supabase
      .from('client_support_messages')
      .select('*', { count: 'exact', head: true })
      .eq('client_id', user.id)
      .eq('is_from_client', false)
      .eq('read_by_client', false);

    setUnreadNotifications(notifCount || 0);
    setUnreadMessages(msgCount || 0);
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-40 h-screen flex flex-col border-r border-border/50 glass-card backdrop-blur-xl transition-all duration-300 overflow-visible",
        isCollapsed ? "w-16" : "w-64",
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-border/50 flex items-center justify-between shrink-0">
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-2">
              <AnimatedLogo size={32} />
              <span className="font-bold text-lg text-gradient">Client Portal</span>
            </div>
            <button
              onClick={onToggleCollapse}
              className={cn(
                "hidden md:flex group h-8 w-8 items-center justify-center",
                "rounded-full border-2 border-primary/20",
                "bg-gradient-to-br from-background via-background to-muted",
                "shadow-lg shadow-primary/10",
                "transition-all duration-300 ease-out",
                "hover:scale-110 hover:shadow-xl hover:shadow-primary/20",
                "hover:border-primary/40 hover:rotate-12",
                "active:scale-95"
              )}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4 text-primary transition-all duration-300 group-hover:scale-110" />
            </button>
          </>
        ) : (
          <div className="w-full flex items-center justify-center gap-2">
            <AnimatedLogo size={24} />
            <button
              onClick={onToggleCollapse}
              className={cn(
                "hidden md:flex z-[60] group h-7 w-7 items-center justify-center",
                "rounded-full border-2 border-primary/20",
                "bg-gradient-to-br from-background via-background to-muted",
                "shadow-lg shadow-primary/10",
                "transition-all duration-300 ease-out",
                "hover:scale-110 hover:shadow-xl hover:shadow-primary/20",
                "hover:border-primary/40 hover:rotate-12",
                "active:scale-95"
              )}
              aria-label="Expand sidebar"
            >
              <ChevronRight className="h-3.5 w-3.5 text-primary transition-all duration-300 group-hover:scale-110" />
            </button>
          </div>
        )}
      </div>


      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        <div className="mb-4">
          {!isCollapsed && (
            <p className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Navigation</p>
          )}
          {menuItems.map((item) => (
            <Button
              key={item.tab}
              variant="ghost"
              onClick={() => onTabChange(item.tab)}
              className={cn(
                "w-full justify-start mb-1 relative transition-all hover-scale",
                isCollapsed ? "px-2" : "px-3",
                activeTab === item.tab && "bg-primary/10 text-primary font-medium hover:bg-primary/20",
              )}
              title={item.title}
            >
              <div className="relative">
                <item.icon className={cn("h-5 w-5", !isCollapsed && "mr-3")} />
                {item.tab === 'chat' && unreadMessages > 0 && (
                  <Badge 
                    variant="destructive" 
                    className={`absolute -top-2 ${isCollapsed ? '-right-2' : '-right-4'} h-4 min-w-4 px-1 flex items-center justify-center text-[10px]`}
                  >
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </Badge>
                )}
              </div>
              {!isCollapsed && (
                <div className="flex items-center justify-between flex-1">
                  <span>{item.title}</span>
                  {item.tab === 'chat' && unreadMessages > 0 && (
                    <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-[10px]">
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </Badge>
                  )}
                </div>
              )}
            </Button>
          ))}
        </div>

        <div className="mt-auto pt-4 border-t border-border/50">
          {!isCollapsed && (
            <p className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Account</p>
          )}
          <Button
            variant="ghost"
            onClick={() => onTabChange('notifications')}
            className={cn(
              "w-full justify-start mb-1 relative transition-all hover-scale",
              isCollapsed ? "px-2" : "px-3",
              activeTab === 'notifications' && "bg-primary/10 text-primary font-medium hover:bg-primary/20"
            )}
            title="Notifications"
          >
            <div className="relative">
              <Bell className={cn("h-5 w-5", !isCollapsed && "mr-3")} />
              {unreadNotifications > 0 && (
                <Badge 
                  variant="destructive" 
                  className={`absolute -top-2 ${isCollapsed ? '-right-2' : '-right-4'} h-4 min-w-4 px-1 flex items-center justify-center text-[10px]`}
                >
                  {unreadNotifications > 99 ? '99+' : unreadNotifications}
                </Badge>
              )}
            </div>
            {!isCollapsed && (
              <div className="flex items-center justify-between flex-1">
                <span>Notifications</span>
                {unreadNotifications > 0 && (
                  <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-[10px]">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </Badge>
                )}
              </div>
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onTabChange('settings')}
            className={cn(
              "w-full justify-start transition-all hover-scale",
              isCollapsed ? "px-2" : "px-3",
              activeTab === 'settings' && "bg-primary/10 text-primary font-medium hover:bg-primary/20"
            )}
            title="Settings"
          >
            <Settings className={cn("h-5 w-5", !isCollapsed && "mr-3")} />
            {!isCollapsed && <span>Settings</span>}
          </Button>
        </div>
      </nav>

      {/* Footer */}
      <div className="p-2 border-t border-border/50 space-y-2 shrink-0">
        {!isCollapsed && (
          <div className="flex flex-col gap-2 px-1 mb-2">
            <LanguageSwitcher />
            <CurrencySwitcher />
          </div>
        )}
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10 transition-all hover-scale",
            isCollapsed ? "px-2" : "px-3",
          )}
          onClick={onSignOut}
          title="Sign Out"
        >
          <LogOut className={cn("h-5 w-5", !isCollapsed && "mr-3")} />
          {!isCollapsed && <span>Sign Out</span>}
        </Button>
      </div>
    </aside>
  );
}
